import type { Selectable } from "kysely"

import type { DB } from "@generated/types"
import {
  ruleLevels,
  outCoverDays,
  reviewPeriodDays,
  ropCoverDays,
  suggestedWithinLimits,
  type MaxLimit,
} from "~/utils/allocationRuleMaths"
import {
  resolveRateOfSaleEntry,
  toRateOfSaleEntry,
  type RateOfSaleBasis,
  type RateOfSaleRow,
  type RateOfSaleSnapshot,
  type ReferenceRosLevel,
  type ReferenceRosUsed,
} from "~/utils/rateOfSale"

export const NO_ROS_BASIS = "NO_ROS_BASIS"
export const EXCLUDED = "EXCLUDED"
// Written by the engine on a line it no longer suggests but a transfer line still points at.
export const NOT_ELIGIBLE = "NOT_ELIGIBLE"

export const LOCKED_LINE_REASON =
  "Lines lock when their transfer is confirmed and open again the next day, or sooner if that transfer is cancelled or completed."

export const MAX_MIN_DISPLAY_QTY = 1000
export const hasMinimum = (mdq: number) => mdq > 0
export const minDisplayQtyText = (mdq: number) => (hasMinimum(mdq) ? mdq.toLocaleString() : "None")
/** Upper bound of a saved maximum or order multiple. */
export const MAX_SETTING_QTY = 1_000_000
/** Variant ids per product settings read, and rows per level per save: 9 bind params per store x SKU row stays under Postgres' 65535 limit. */
export const MAX_PRODUCT_SETTING_ROWS = 5000

// The default when neither AllocationStoreSkuSetting nor AllocationVariantSetting sets one.
export const ORDER_MULTIPLE = 1

/** Which level a line's setting came from: store x SKU, variant (all stores) or the default. */
export type SettingSource = "storeSku" | "variant" | "default"
export type SettingSources = Record<
  "excluded" | "minDisplayQty" | "maxQty" | "orderMultiple",
  SettingSource
>
export type ResolvedSetting<T> = { value: T; source: SettingSource }
/** A minimum above the maximum, set on different levels: the more specific one won and the other was ignored. */
export type SettingsConflict = {
  winner: "minDisplayQty" | "maxQty"
  ignored: ResolvedSetting<number>
}
/** Where a line's settings came from; the run stores it beside the engine's meta. */
export type SettingDiagnostics = {
  settingSources: SettingSources | null
  settingsConflict: SettingsConflict | null
}

type AllocationRuleRow = Selectable<DB["AllocationRule"]>

export type EngineRule = Pick<
  AllocationRuleRow,
  | "id"
  | "unit"
  | "minStock"
  | "upToStock"
  | "maxStock"
  | "safetyStockDays"
  | "freq"
  | "byDay"
  | "interval"
> &
  Partial<Pick<AllocationRuleRow, "dtstart" | "cutoff">>

export type RateOfSaleSource = Exclude<RateOfSaleBasis, "none">

export type RunRateOfSaleCalculation = Pick<
  RateOfSaleSnapshot,
  "windowDays" | "weights" | "minDaysWithStock" | "daysWithInventory" | "buckets"
> & {
  /** Last day of the sales data, "YYYY-MM-DD". */
  asOf: string
}

export type EngineLine = {
  ros: number | null
  rosSource: RateOfSaleSource | null
  /** Null unless rosSource is "reference". */
  referenceRos: ReferenceRosUsed | null
  rosCalculation: RunRateOfSaleCalculation | null
  /** The gap after this run's departure (reviewPeriodDaysFrom); the rule's average gap when absent. */
  reviewPeriodDays?: number
  daysWithInventory: number | null
  transitDays: number
  excluded: boolean
  /** 0 for no minimum. */
  minDisplayQty: number
  /** No cap when absent or null. */
  maxQty?: number | null
  /** ORDER_MULTIPLE when absent. */
  orderMultiple?: number
  onHand: number
  inTransit: number
}

export type SuggestedQuantity = {
  quantity: number
  reasonCode: typeof NO_ROS_BASIS | typeof EXCLUDED | null
  meta: {
    ruleId: string
    ros: number | null
    rosSource: RateOfSaleSource | null
    referenceRos: ReferenceRosUsed | null
    rosCalculation: RunRateOfSaleCalculation | null
    daysWithInventory: number | null
    transitDays: number
    safetyStockDays: number
    reviewPeriodDays: number
    ropCoverDays: number
    outCoverDays: number
    mdq: number
    orderMultiple: number
    maxQty: number | null
    excluded: boolean
    onHand: number
    inTransit: number
    position: number
    reorderPoint: number | null
    orderUpTo: number | null
    overstock: number | null
    rawNeed: number | null
    /** Why the maximum lowered the quantity; null when it did not. */
    maxLimit: MaxLimit
    /** The minimum display quantity raised the quantity above what the levels asked for. */
    minimumRaised: boolean | null
  }
}
/** AllocationPlanLine.meta as a run writes it: the engine's meta plus where the settings came from. */
export type SuggestedQuantityMeta = SuggestedQuantity["meta"] & SettingDiagnostics
export type SuggestedQuantityReason = SuggestedQuantity["reasonCode"]
export type AllocationPlanLineReason = SuggestedQuantityReason | typeof NOT_ELIGIBLE

/** AllocationPlanLine.meta as stored: lines from older runs lack the fields the engine added since. */
type AddedSinceFirstRun =
  | "overstock"
  | "rosSource"
  | "referenceRos"
  | "rosCalculation"
  | "maxQty"
  | "maxLimit"
  | "minimumRaised"
  | "settingsConflict"
  | "excluded"
  | "settingSources"
export type StoredSuggestedQuantityMeta = Omit<SuggestedQuantityMeta, AddedSinceFirstRun> &
  Partial<Pick<SuggestedQuantityMeta, AddedSinceFirstRun>>

/** The levels a run stored on a line, as the grid and line inspector show them. */
export type EngineLevels = Pick<
  SuggestedQuantityMeta,
  | "onHand"
  | "inTransit"
  | "position"
  | "transitDays"
  | "safetyStockDays"
  | "reviewPeriodDays"
  | "ropCoverDays"
  | "outCoverDays"
  | "mdq"
  | "orderMultiple"
> & {
  ros: number
  reorderPoint: number
  orderUpTo: number
  /** Null on lines from runs before the engine stored it. */
  overstock: number | null
  /** These three are null on lines from runs before the engine stored them. */
  maxQty: number | null
  maxLimit: MaxLimit
  minimumRaised: boolean | null
}

/**
 * The minimum, maximum and order multiple a run applied to a line. Exclusion is the
 * line's EXCLUDED reason code. The sources come only from getLineRunDetails: in
 * the lines payload they would add megabytes to a large grid.
 */
export type RunLineSettings = {
  minDisplayQty: number
  maxQty: number | null
  orderMultiple: number
  settingSources?: SettingSources | null
  settingsConflict?: SettingsConflict | null
}

export type EngineRateOfSaleRow = RateOfSaleRow & {
  asOf: string
  referenceRateOfSaleType: ReferenceRosLevel | null
  referenceRateOfSaleLines: number | null
}

/**
 * What the engine reads from a line's rate-of-sale row: the SKU's own rate, else the
 * Reference ROS, else none, with the calculation behind it.
 */
export function engineRateOfSaleInputs(
  row: EngineRateOfSaleRow | undefined,
): Pick<EngineLine, "ros" | "rosSource" | "referenceRos" | "rosCalculation" | "daysWithInventory"> {
  if (!row) {
    return {
      ros: null,
      rosSource: null,
      referenceRos: null,
      rosCalculation: null,
      daysWithInventory: null,
    }
  }
  const snapshot = row.calculationSnapshot
  const { rateOfSale, rateOfSaleBasis } = resolveRateOfSaleEntry(toRateOfSaleEntry(row))
  const { referenceRateOfSaleType: level, referenceRateOfSaleLines: lines } = row
  return {
    ...(rateOfSaleBasis === "none"
      ? { ros: null, rosSource: null }
      : { ros: rateOfSale, rosSource: rateOfSaleBasis }),
    referenceRos:
      rateOfSaleBasis === "reference" && level != null && lines != null ? { level, lines } : null,
    daysWithInventory: snapshot?.daysWithInventory ?? null,
    rosCalculation: snapshot
      ? {
          asOf: row.asOf,
          windowDays: snapshot.windowDays,
          weights: snapshot.weights,
          minDaysWithStock: snapshot.minDaysWithStock,
          daysWithInventory: snapshot.daysWithInventory,
          buckets: snapshot.buckets,
        }
      : null,
  }
}

/** What a line's run read for its rate of sale, as the inspector explains it. */
export type RunRateOfSale =
  | { kind: "not-stored" }
  | { kind: "no-row" }
  | { kind: "own"; ros: number; calculation: RunRateOfSaleCalculation | null }
  | { kind: "reference"; ros: number; calculation: RunRateOfSaleCalculation | null }
  | { kind: "below-minimum"; calculation: RunRateOfSaleCalculation }

export function runRateOfSale(meta: StoredSuggestedQuantityMeta | null): RunRateOfSale {
  // Runs before the engine stored the calculation have no key at all; null means no row.
  if (meta?.rosCalculation === undefined) return { kind: "not-stored" }
  const calculation = meta.rosCalculation
  if (meta.ros != null && meta.rosSource === "own")
    return { kind: "own", ros: meta.ros, calculation }
  if (meta.ros != null && meta.rosSource === "reference") {
    return { kind: "reference", ros: meta.ros, calculation }
  }
  if (calculation == null) return { kind: "no-row" }
  return { kind: "below-minimum", calculation }
}

/** Null when the run set no levels: excluded, or no rate-of-sale basis. */
export function engineLevels(meta: StoredSuggestedQuantityMeta | null): EngineLevels | null {
  if (meta?.ros == null || meta.reorderPoint == null || meta.orderUpTo == null) return null
  return {
    ros: meta.ros,
    onHand: meta.onHand,
    inTransit: meta.inTransit,
    position: meta.position,
    transitDays: meta.transitDays,
    safetyStockDays: meta.safetyStockDays,
    reviewPeriodDays: meta.reviewPeriodDays,
    ropCoverDays: meta.ropCoverDays,
    outCoverDays: meta.outCoverDays,
    mdq: meta.mdq,
    orderMultiple: meta.orderMultiple,
    reorderPoint: meta.reorderPoint,
    orderUpTo: meta.orderUpTo,
    overstock: meta.overstock ?? null,
    maxQty: meta.maxQty ?? null,
    maxLimit: meta.maxLimit ?? null,
    minimumRaised: meta.minimumRaised ?? null,
  }
}

/** A line's levels as the grid shows them, and the run's settings only where it set no levels (excluded, no ROS basis). */
export function lineRunView(meta: StoredSuggestedQuantityMeta | null) {
  const levels = engineLevels(meta)
  return { engineLevels: levels, runSettings: levels ? null : runLineSettings(meta) }
}

export function runLineSettings(
  meta: StoredSuggestedQuantityMeta | null,
  { withSources = false } = {},
): RunLineSettings | null {
  if (meta == null) return null
  const values = {
    minDisplayQty: meta.mdq,
    maxQty: meta.maxQty ?? null,
    orderMultiple: meta.orderMultiple,
  }
  if (!withSources) return values
  return {
    ...values,
    settingSources: meta.settingSources ?? null,
    settingsConflict: meta.settingsConflict ?? null,
  }
}

export function suggestQuantity(rule: EngineRule, line: EngineLine): SuggestedQuantity {
  const mdq = line.minDisplayQty
  const orderMultiple = line.orderMultiple ?? ORDER_MULTIPLE
  const maxQty = line.maxQty ?? null
  const position = line.onHand + line.inTransit
  const reviewDays = line.reviewPeriodDays ?? reviewPeriodDays(rule)

  const inputs = {
    ruleId: rule.id,
    ros: line.ros,
    rosSource: line.rosSource,
    referenceRos: line.referenceRos,
    rosCalculation: line.rosCalculation,
    daysWithInventory: line.daysWithInventory,
    transitDays: line.transitDays,
    safetyStockDays: rule.safetyStockDays,
    reviewPeriodDays: reviewDays,
    ropCoverDays: ropCoverDays(rule, line.transitDays),
    outCoverDays: outCoverDays(rule, line.transitDays, reviewDays),
    mdq,
    orderMultiple,
    maxQty,
    excluded: line.excluded,
    onHand: line.onHand,
    inTransit: line.inTransit,
    position,
  }

  if (line.excluded || line.ros == null) {
    return {
      quantity: 0,
      reasonCode: line.excluded ? EXCLUDED : NO_ROS_BASIS,
      meta: {
        ...inputs,
        reorderPoint: null,
        orderUpTo: null,
        overstock: null,
        rawNeed: null,
        maxLimit: null,
        minimumRaised: null,
      },
    }
  }

  const lv = ruleLevels(rule, {
    ros: line.ros,
    transitDays: line.transitDays,
    reviewPeriodDays: reviewDays,
  })
  const { quantity, maxLimit, minimumRaised } = suggestedWithinLimits(lv, position, {
    ros: line.ros,
    transitDays: line.transitDays,
    mdq,
    maxQty,
    orderMultiple,
  })
  return {
    quantity,
    reasonCode: null,
    meta: {
      ...inputs,
      ...lv,
      rawNeed: lv.orderUpTo - position,
      maxLimit,
      minimumRaised,
    },
  }
}
